import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';
import ScrollToTop from '../../components/feature/ScrollToTop';
import HeroSection from './components/HeroSection';
import WhatIsSection from './components/WhatIsSection';
import EventTypesSection from './components/EventTypesSection';
import BenefitsSection from './components/BenefitsSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import PacotesBox from '@/components/feature/PacotesBox';
import NossosFones from '@/components/feature/NossosFones';

export default function PalestraSilenciosaEventosPage() {
  useEffect(() => {
    document.title = 'Palestra Silenciosa em Eventos | Fones Sem Fio para Conferências — STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa em eventos corporativos, treinamentos, conferências multilíngues e festivais no Brasil. Fones sem fio com até 10 canais simultâneos. +500 eventos · STIB (11) 91777-6227'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://stibpalestrasilenciosasp.com.br/palestra-silenciosa-eventos');

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    // Schema.org — Service + BreadcrumbList + FAQPage + HowTo
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${siteUrl}/palestra-silenciosa-eventos#service`,
          name: 'Palestra Silenciosa em Eventos',
          description:
            'Tecnologia de fones sem fio para palestras, conferências, treinamentos corporativos, festas silent disco e eventos multilíngues em todo o Brasil. Até 10 canais simultâneos, suporte técnico presencial.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'STIB — Palestra Silenciosa Brasil',
            url: siteUrl,
            telephone: '+55-11-91777-6227',
          },
          areaServed: { '@type': 'Country', name: 'Brasil' },
          serviceType: 'Locação de Equipamento de Áudio Sem Fio',
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            areaServed: 'BR',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Palestra Silenciosa em Eventos',
              item: `${siteUrl}/palestra-silenciosa-eventos`,
            },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Qual o número mínimo e máximo de participantes?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'A STIB atende eventos a partir de 20 participantes, com teto atual de 1.500 fones simultâneos.',
              },
            },
            {
              '@type': 'Question',
              name: 'Quanto tempo antes preciso contratar a palestra silenciosa?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Para eventos de até 200 pessoas, recomendamos 15 dias de antecedência. Para eventos maiores, 30 dias.',
              },
            },
            {
              '@type': 'Question',
              name: 'A STIB fornece suporte técnico durante o evento?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sim. Um técnico especializado fica dedicado ao evento do início ao fim, com equipamentos de backup disponíveis.',
              },
            },
          ],
        },
        {
          '@type': 'HowTo',
          '@id': `${siteUrl}/palestra-silenciosa-eventos#howto`,
          name: 'Como funciona a palestra silenciosa em eventos',
          description:
            'Passo a passo de como a tecnologia de palestra silenciosa da STIB funciona em eventos corporativos, conferências e festivais. Fones sem fio com até 10 canais simultâneos, latência menor que 5ms e suporte técnico presencial.',
          image: {
            '@type': 'ImageObject',
            url: '/img/stib/fone-participante-close.webp',
          },
          totalTime: 'PT1H',
          estimatedCost: {
            '@type': 'MonetaryAmount',
            currency: 'BRL',
            value: 'Sob consulta',
          },
          supply: [
            {
              '@type': 'HowToSupply',
              name: 'Fones sem fio STIB com receptor multi-canal',
            },
            {
              '@type': 'HowToSupply',
              name: 'Transmissor de radiofrequência',
            },
            {
              '@type': 'HowToSupply',
              name: 'Microfone sem fio para palestrante',
            },
          ],
          tool: [
            {
              '@type': 'HowToTool',
              name: 'Equipamento de áudio sem fio STIB',
            },
            {
              '@type': 'HowToTool',
              name: 'Suporte técnico especializado presencial',
            },
          ],
          step: [
            {
              '@type': 'HowToStep',
              position: 1,
              name: 'Palestrante fala normalmente',
              text:
                'O palestrante usa um microfone sem fio comum. Não são necessárias cabines de tradução, cabeamentos especiais ou equipamentos extras no palco.',
              image: {
                '@type': 'ImageObject',
                url: '/img/stib/equip-microfones.webp',
              },
              url: `${siteUrl}/palestra-silenciosa-eventos#como-funciona`,
            },
            {
              '@type': 'HowToStep',
              position: 2,
              name: 'Sinal transmitido em radiofrequência',
              text:
                'O transmissor captura o áudio e envia em radiofrequência para todos os fones simultaneamente, com latência abaixo de 5ms — garantindo sincronia perfeita com os lábios do palestrante.',
              image: {
                '@type': 'ImageObject',
                url: '/img/stib/congresso-coffee.webp',
              },
              url: `${siteUrl}/palestra-silenciosa-eventos#como-funciona`,
            },
            {
              '@type': 'HowToStep',
              position: 3,
              name: 'Participante escolhe o canal desejado',
              text:
                'Cada participante recebe um fone sem fio e seleciona o canal de sua preferência — palestrante principal, idioma de interpretação ou trilha paralela. O volume individual é ajustável.',
              image: {
                '@type': 'ImageObject',
                url: '/img/stib/fone-detalhe-mao.webp',
              },
              url: `${siteUrl}/palestra-silenciosa-eventos#como-funciona`,
            },
            {
              '@type': 'HowToStep',
              position: 4,
              name: 'Experiência de áudio imersiva',
              text:
                'O participante recebe áudio de alta fidelidade diretamente ao ouvido, sem reverberação de caixas de som, sem ruído ambiente e sem distrações — propiciando foco total no conteúdo.',
              image: {
                '@type': 'ImageObject',
                url: '/img/stib/fone-participante-close.webp',
              },
              url: `${siteUrl}/palestra-silenciosa-eventos#como-funciona`,
            },
          ],
        },
      ],
    };

    const existing = document.getElementById('ps-eventos-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'ps-eventos-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('ps-eventos-schema');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingCTA />
      <ScrollToTop />
      <main>
        <HeroSection />
        <WhatIsSection />
        <EventTypesSection />
        <BenefitsSection />
        <TestimonialsSection />
        <FAQSection />
      <NossosFones />
      <PacotesBox texto="Empresas e organizadores com eventos recorrentes (convenções, treinamentos, congressos anuais) podem fechar um pacote de eventos com valor fechado e economia em relação à contratação avulsa." />
      </main>
      <Footer />
    </div>
  );
}
